import { useEffect, useState, useMemo } from "react";
import dayjs from "dayjs";
import "dayjs/locale/fr";
import weekday from "dayjs/plugin/weekday";
dayjs.extend(weekday);
dayjs.locale("fr");
import api, { API, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { toast } from "sonner";
import { Bell, Euro, Users, CalendarDays, Wallet, Loader2, Trash2, Plus, FileText, ChevronLeft, ChevronRight, MapPin, Phone, Ban, TrendingUp } from "lucide-react";

const RIDE_LABELS = { course_simple: "Course simple", aller_retour: "Aller-retour", mise_a_disposition: "Mise à disposition", transfert_aeroport: "Transfert aéroport" };
const STATUS = { confirmee: ["Confirmée", "bg-blue-500/15 text-blue-300 border-blue-500/30"], en_cours: ["En cours", "bg-amber-500/15 text-amber-300 border-amber-500/30"], terminee: ["Terminée", "bg-emerald-500/15 text-emerald-300 border-emerald-500/30"], annulee: ["Annulée", "bg-red-500/15 text-red-300 border-red-500/30"] };

export default function DriverDashboard() {
  const { user } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [clients, setClients] = useState([]);
  const [packages, setPackages] = useState([]);
  const [pricing, setPricing] = useState(null);
  const [stats, setStats] = useState(null);
  const [notifs, setNotifs] = useState({ items: [], unread: 0 });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [b, c, p, pr, s, n] = await Promise.all([
        api.get("/bookings"), api.get("/clients"), api.get("/packages"),
        api.get("/pricing"), api.get("/stats"), api.get("/notifications"),
      ]);
      setBookings(b.data); setClients(c.data); setPackages(p.data);
      setPricing(pr.data); setStats(s.data); setNotifs(n.data);
    } catch { toast.error("Erreur de chargement"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading || !pricing) return <div className="min-h-screen bg-[#0B0F17] flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#39FF14]" /></div>;

  return (
    <div className="min-h-screen bg-[#0B0F17] grain">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 animate-fade-up">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="font-display text-3xl font-bold text-slate-50">Tableau de bord</h1>
            <p className="text-slate-400 mt-1">Bonjour {user?.name}, voici l'activité de votre journée.</p>
          </div>
          <NotifBell notifs={notifs} onOpen={async () => { await api.post("/notifications/read"); setNotifs((n) => ({ ...n, unread: 0 })); }} />
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
          <StatCard icon={CalendarDays} label="Courses" value={stats.total_bookings} />
          <StatCard icon={Euro} label="Revenu total" value={`${stats.revenue.toFixed(0)} €`} gold />
          <StatCard icon={Users} label="Clients" value={stats.clients} />
          <StatCard icon={TrendingUp} label="En attente" value={`${stats.pending.toFixed(0)} €`} />
        </div>

        <Tabs defaultValue="calendar" className="mt-8">
          <TabsList className="bg-[#131926] border border-[#1E293B] flex-wrap h-auto">
            <TabsTrigger value="calendar" data-testid="tab-calendar">Calendrier</TabsTrigger>
            <TabsTrigger value="availability" data-testid="tab-availability">Disponibilités</TabsTrigger>
            <TabsTrigger value="clients" data-testid="tab-clients">Clients</TabsTrigger>
            <TabsTrigger value="pricing" data-testid="tab-pricing">Tarifs</TabsTrigger>
            <TabsTrigger value="payments" data-testid="tab-payments">Paiements</TabsTrigger>
          </TabsList>

          <TabsContent value="calendar" className="mt-6"><CalendarView bookings={bookings} onReload={load} /></TabsContent>
          <TabsContent value="availability" className="mt-6"><Availability onReload={load} /></TabsContent>
          <TabsContent value="clients" className="mt-6"><ClientsView clients={clients} /></TabsContent>
          <TabsContent value="pricing" className="mt-6"><PricingView pricing={pricing} packages={packages} onReload={load} /></TabsContent>
          <TabsContent value="payments" className="mt-6"><PaymentsView bookings={bookings} stats={stats} /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

const StatCard = ({ icon: Icon, label, value, gold }) => (
  <div className="card-surface rounded-xl p-5">
    <div className={`h-9 w-9 rounded-lg flex items-center justify-center mb-3 ${gold ? "bg-[#39FF14]/15" : "bg-[#1C2436]"}`}><Icon className={`h-4 w-4 ${gold ? "text-[#39FF14]" : "text-slate-300"}`} /></div>
    <p className="text-xs uppercase tracking-wider text-slate-500">{label}</p>
    <p className={`font-mono text-2xl font-bold mt-1 ${gold ? "text-[#39FF14]" : "text-slate-100"}`}>{value}</p>
  </div>
);

function NotifBell({ notifs, onOpen }) {
  return (
    <Popover onOpenChange={(o) => o && onOpen()}>
      <PopoverTrigger asChild>
        <Button variant="ghost" className="relative text-slate-300 hover:bg-[#1C2436]" data-testid="notifications-bell">
          <Bell className="h-5 w-5" />
          {notifs.unread > 0 && <span className="absolute top-1 right-1 h-4 w-4 rounded-full bg-[#39FF14] text-[#0B0F17] text-[10px] font-bold flex items-center justify-center">{notifs.unread}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 bg-[#131926] border-[#1E293B] p-0" align="end">
        <div className="p-3 border-b border-[#1E293B]"><p className="font-medium text-slate-100 text-sm">Notifications</p></div>
        <div className="max-h-80 overflow-auto">
          {notifs.items.length === 0 && <p className="p-4 text-sm text-slate-500">Aucune notification</p>}
          {notifs.items.map((n) => (
            <div key={n.id} className="p-3 border-b border-[#1E293B] hover:bg-[#1C2436]">
              <p className="text-sm font-medium text-slate-100">{n.title}</p>
              <p className="text-xs text-slate-400 mt-0.5">{n.body}</p>
              <p className="text-[10px] text-slate-600 mt-1">{dayjs(n.created_at).format("DD/MM HH:mm")}</p>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function CalendarView({ bookings, onReload }) {
  const [mode, setMode] = useState("month");
  const [cursor, setCursor] = useState(dayjs());
  const [selected, setSelected] = useState(null);

  const byDate = useMemo(() => {
    const m = {};
    bookings.forEach((b) => { (m[b.date] = m[b.date] || []).push(b); });
    return m;
  }, [bookings]);

  const setStatus = async (id, status) => {
    try { await api.patch(`/bookings/${id}/status`, { status }); toast.success("Statut mis à jour"); onReload(); setSelected(null); }
    catch { toast.error("Erreur"); }
  };

  const renderMonth = () => {
    const start = cursor.startOf("month").startOf("week");
    const days = Array.from({ length: 42 }, (_, i) => start.add(i, "day"));
    return (
      <div className="grid grid-cols-7 gap-1.5">
        {["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"].map((d) => <div key={d} className="text-center text-xs text-slate-500 font-medium py-2">{d}</div>)}
        {days.map((d) => {
          const key = d.format("YYYY-MM-DD");
          const items = byDate[key] || [];
          const isMonth = d.month() === cursor.month();
          const isToday = d.isSame(dayjs(), "day");
          return (
            <button key={key} onClick={() => items.length && setSelected({ date: key, items })}
              className={`min-h-[72px] rounded-lg border p-2 text-left transition-all ${isMonth ? "bg-[#0B0F17] border-[#1E293B]" : "bg-transparent border-transparent opacity-40"} ${isToday ? "ring-1 ring-[#39FF14]" : ""} ${items.length ? "hover:border-[#39FF14]/50" : ""}`}>
              <span className={`text-xs font-medium ${isToday ? "text-[#39FF14]" : "text-slate-400"}`}>{d.date()}</span>
              {items.slice(0, 2).map((b) => (
                <div key={b.id} className="mt-1 text-[10px] truncate px-1 py-0.5 rounded bg-[#39FF14]/15 text-[#39FF14]">{b.time} {b.client_name.split(" ")[0]}</div>
              ))}
              {items.length > 2 && <div className="text-[10px] text-slate-500 mt-0.5">+{items.length - 2}</div>}
            </button>
          );
        })}
      </div>
    );
  };

  const renderList = (days) => (
    <div className={`grid gap-3 ${days.length > 1 ? "md:grid-cols-7" : ""}`}>
      {days.map((d) => {
        const key = d.format("YYYY-MM-DD");
        const items = (byDate[key] || []).sort((a, b) => a.time.localeCompare(b.time));
        return (
          <div key={key} className="card-surface rounded-xl p-3 min-h-[120px]">
            <p className="text-xs font-medium text-slate-300 mb-2">{d.format("ddd DD/MM")}</p>
            {items.length === 0 && <p className="text-[11px] text-slate-600">—</p>}
            {items.map((b) => {
              const st = STATUS[b.status] || STATUS.confirmee;
              return (
                <button key={b.id} onClick={() => setSelected({ date: key, items: [b] })} className="w-full text-left mb-1.5 rounded-lg bg-[#0B0F17] border border-[#1E293B] p-2 hover:border-[#39FF14]/50">
                  <div className="flex items-center justify-between"><span className="font-mono text-xs text-[#39FF14]">{b.time}</span><Badge className={`${st[1]} border text-[9px] px-1.5`}>{st[0]}</Badge></div>
                  <p className="text-xs text-slate-200 mt-1 truncate">{b.client_name}</p>
                  <p className="text-[10px] text-slate-500 truncate">{RIDE_LABELS[b.ride_type]}</p>
                </button>
              );
            })}
          </div>
        );
      })}
    </div>
  );

  const weekDays = Array.from({ length: 7 }, (_, i) => cursor.startOf("week").add(i, "day"));

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <div className="flex items-center gap-2">
          <Button size="icon" variant="ghost" className="text-slate-300 hover:bg-[#1C2436]" onClick={() => setCursor(cursor.subtract(1, mode === "month" ? "month" : mode === "week" ? "week" : "day"))}><ChevronLeft className="h-4 w-4" /></Button>
          <span className="font-display font-semibold text-slate-100 min-w-[160px] text-center">{mode === "day" ? cursor.format("dddd DD MMM") : mode === "week" ? `Semaine du ${cursor.startOf("week").format("DD MMM")}` : cursor.format("MMMM YYYY")}</span>
          <Button size="icon" variant="ghost" className="text-slate-300 hover:bg-[#1C2436]" onClick={() => setCursor(cursor.add(1, mode === "month" ? "month" : mode === "week" ? "week" : "day"))}><ChevronRight className="h-4 w-4" /></Button>
        </div>
        <Tabs value={mode} onValueChange={setMode}>
          <TabsList className="bg-[#131926] border border-[#1E293B]">
            <TabsTrigger value="day" data-testid="dashboard-calendar-day-tab">Jour</TabsTrigger>
            <TabsTrigger value="week" data-testid="dashboard-calendar-week-tab">Semaine</TabsTrigger>
            <TabsTrigger value="month" data-testid="dashboard-calendar-month-tab">Mois</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {mode === "month" && renderMonth()}
      {mode === "week" && renderList(weekDays)}
      {mode === "day" && renderList([cursor])}

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="bg-[#131926] border-[#1E293B] text-slate-100 max-w-md">
          <DialogHeader><DialogTitle className="font-display">{selected && dayjs(selected.date).format("dddd DD MMMM")}</DialogTitle></DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-auto">
            {selected?.items.map((b) => {
              const st = STATUS[b.status] || STATUS.confirmee;
              return (
                <div key={b.id} className="rounded-xl bg-[#0B0F17] border border-[#1E293B] p-4">
                  <div className="flex items-center justify-between"><span className="font-mono text-[#39FF14]">{b.time} · {b.reference}</span><Badge className={`${st[1]} border`}>{st[0]}</Badge></div>
                  <p className="text-sm font-medium mt-2">{b.client_name} · {b.client_phone}</p>
                  <p className="text-sm text-slate-300 mt-1 flex items-start gap-1.5"><MapPin className="h-3.5 w-3.5 text-[#39FF14] mt-0.5 shrink-0" />{b.pickup}{b.dropoff ? ` → ${b.dropoff}` : ""}</p>
                  <p className="text-xs text-slate-500 mt-1">{RIDE_LABELS[b.ride_type]} · {b.price?.toFixed(2)} € · {b.payment_method === "card" ? "Carte" : "À bord"}</p>
                  <div className="flex gap-2 mt-3">
                    <Select value={b.status} onValueChange={(v) => setStatus(b.id, v)}>
                      <SelectTrigger className="bg-[#131926] border-[#1E293B] h-9 text-xs" data-testid="booking-status-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{Object.keys(STATUS).map((s) => <SelectItem key={s} value={s}>{STATUS[s][0]}</SelectItem>)}</SelectContent>
                    </Select>
                    <a href={`${API}/invoices/${b.id}`} target="_blank" rel="noreferrer" className="flex-1">
                      <Button className="w-full h-9 bg-[#1C2436] hover:bg-[#243049] text-slate-100 text-xs" data-testid="dashboard-generate-invoice-button"><FileText className="h-3.5 w-3.5 mr-1.5" />Facture PDF</Button>
                    </a>
                  </div>
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Availability({ onReload }) {
  const [blocks, setBlocks] = useState([]);
  const [form, setForm] = useState({ date: "", time: "", reason: "Congés" });
  const load = () => api.get("/availability").then(({ data }) => setBlocks(data));
  useEffect(() => { load(); }, []);
  const add = async () => {
    if (!form.date || !form.time) { toast.error("Date et heure requises"); return; }
    try { await api.post("/availability", form); toast.success("Créneau bloqué"); load(); onReload(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const remove = async (id) => { await api.delete(`/availability/${id}`); load(); };
  return (
    <div className="grid md:grid-cols-2 gap-6">
      <div className="card-surface rounded-xl p-6">
        <h3 className="font-display font-semibold text-slate-100 flex items-center gap-2"><Ban className="h-4 w-4 text-[#39FF14]" />Bloquer un créneau</h3>
        <p className="text-sm text-slate-400 mt-1">Indiquez vos indisponibilités (congés, pause).</p>
        <div className="grid grid-cols-2 gap-3 mt-4">
          <div><Label className="text-slate-300">Date</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1.5 bg-[#0B0F17] border-[#1E293B] text-slate-100" data-testid="block-date-input" /></div>
          <div><Label className="text-slate-300">Heure</Label><Input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} className="mt-1.5 bg-[#0B0F17] border-[#1E293B] text-slate-100" data-testid="block-time-input" /></div>
        </div>
        <Input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="Motif" className="mt-3 bg-[#0B0F17] border-[#1E293B] text-slate-100" data-testid="block-reason-input" />
        <Button onClick={add} className="w-full mt-4 bg-[#39FF14] hover:bg-[#5CFF45] text-[#0B0F17] font-semibold" data-testid="dashboard-block-slot-button"><Plus className="h-4 w-4 mr-1.5" />Bloquer</Button>
      </div>
      <div className="card-surface rounded-xl p-6">
        <h3 className="font-display font-semibold text-slate-100">Créneaux bloqués ({blocks.length})</h3>
        <div className="mt-4 space-y-2 max-h-80 overflow-auto">
          {blocks.length === 0 && <p className="text-sm text-slate-500">Aucun créneau bloqué.</p>}
          {blocks.map((b) => (
            <div key={b.id} className="flex items-center justify-between rounded-lg bg-[#0B0F17] border border-[#1E293B] p-3">
              <div><p className="text-sm text-slate-200 font-mono">{b.date} · {b.time}</p><p className="text-xs text-slate-500">{b.reason}</p></div>
              <Button size="icon" variant="ghost" className="text-slate-500 hover:text-red-400" onClick={() => remove(b.id)}><Trash2 className="h-4 w-4" /></Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ClientsView({ clients }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(null);
  const filtered = clients.filter((c) => c.name.toLowerCase().includes(q.toLowerCase()) || c.email.toLowerCase().includes(q.toLowerCase()));
  return (
    <div>
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un client…" className="max-w-sm bg-[#0B0F17] border-[#1E293B] text-slate-100 mb-5" data-testid="dashboard-client-search-input" />
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((c) => (
          <button key={c.email} onClick={() => setOpen(c)} className="card-surface rounded-xl p-5 text-left hover:border-[#39FF14]/50 transition-all">
            <div className="flex items-center justify-between">
              <div className="h-10 w-10 rounded-full bg-[#39FF14]/15 flex items-center justify-center text-[#39FF14] font-semibold">{c.name.charAt(0)}</div>
              {c.free_rides_earned > 0 && <Badge className="bg-[#39FF14] text-[#0B0F17] text-[10px]">{c.free_rides_earned} offerte(s)</Badge>}
            </div>
            <p className="font-medium text-slate-100 mt-3">{c.name}</p>
            <p className="text-xs text-slate-500 flex items-center gap-1"><Phone className="h-3 w-3" />{c.phone || "—"}</p>
            <div className="flex gap-4 mt-3 text-xs">
              <span className="text-slate-400">{c.rides} course{c.rides > 1 ? "s" : ""}</span>
              <span className="text-[#39FF14] font-mono">{c.total_spent.toFixed(0)} €</span>
            </div>
            <div className="flex gap-1 mt-3">{Array.from({ length: 10 }).map((_, i) => <div key={i} className={`h-1.5 flex-1 rounded-full ${i < c.loyalty_rides ? "bg-[#39FF14]" : "bg-[#1E293B]"}`} />)}</div>
          </button>
        ))}
      </div>
      <Dialog open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent className="bg-[#131926] border-[#1E293B] text-slate-100 max-w-lg">
          <DialogHeader><DialogTitle className="font-display">{open?.name}</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-400">{open?.email} · {open?.phone}</p>
          <div className="space-y-2 max-h-[50vh] overflow-auto mt-2">
            {open?.history.map((b) => (
              <div key={b.id} className="rounded-lg bg-[#0B0F17] border border-[#1E293B] p-3 flex justify-between">
                <div><p className="text-sm text-slate-200">{RIDE_LABELS[b.ride_type]}</p><p className="text-xs text-slate-500">{b.date} · {b.pickup}{b.dropoff ? ` → ${b.dropoff}` : ""}</p></div>
                <span className="font-mono text-[#39FF14] text-sm">{b.price?.toFixed(2)} €</span>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PricingView({ pricing, packages, onReload }) {
  const [p, setP] = useState(pricing);
  const [pkg, setPkg] = useState({ label: "", price: "" });
  const save = async () => {
    try {
      await api.put("/pricing", { ...p, base_fare: Number(p.base_fare), price_per_km: Number(p.price_per_km), price_per_hour: Number(p.price_per_hour), min_fare: Number(p.min_fare), night_surcharge_pct: Number(p.night_surcharge_pct), vat_rate: Number(p.vat_rate) });
      toast.success("Tarifs enregistrés"); onReload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const addPkg = async () => {
    if (!pkg.label || !pkg.price) { toast.error("Libellé et prix requis"); return; }
    await api.post("/packages", { label: pkg.label, price: Number(pkg.price) }); setPkg({ label: "", price: "" }); toast.success("Forfait ajouté"); onReload();
  };
  const delPkg = async (id) => { await api.delete(`/packages/${id}`); onReload(); };
  const field = (k, label, unit) => (
    <div><Label className="text-slate-300">{label}</Label>
      <div className="relative mt-1.5"><Input type="number" step="0.1" value={p[k]} onChange={(e) => setP({ ...p, [k]: e.target.value })} className="bg-[#0B0F17] border-[#1E293B] text-slate-100 pr-12" data-testid={`pricing-${k}-input`} />
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-500">{unit}</span></div>
    </div>
  );
  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="card-surface rounded-xl p-6">
        <h3 className="font-display font-semibold text-slate-100">Paramètres tarifaires</h3>
        <div className="grid grid-cols-2 gap-4 mt-4">
          {field("base_fare", "Prise en charge", "€")}
          <div data-testid="dashboard-price-per-km-input">{field("price_per_km", "Prix au km", "€/km")}</div>
          <div data-testid="dashboard-price-per-hour-input">{field("price_per_hour", "Prix à l'heure", "€/h")}</div>
          {field("min_fare", "Course minimum", "€")}
          {field("night_surcharge_pct", "Majoration nuit", "%")}
          {field("vat_rate", "TVA", "%")}
        </div>
        <div className="grid grid-cols-1 gap-4 mt-4 border-t border-[#1E293B] pt-4">
          <div><Label className="text-slate-300">Nom société</Label><Input value={p.company_name} onChange={(e) => setP({ ...p, company_name: e.target.value })} className="mt-1.5 bg-[#0B0F17] border-[#1E293B] text-slate-100" /></div>
          <div><Label className="text-slate-300">SIRET</Label><Input value={p.siret} onChange={(e) => setP({ ...p, siret: e.target.value })} className="mt-1.5 bg-[#0B0F17] border-[#1E293B] text-slate-100" /></div>
        </div>
        <Button onClick={save} className="w-full mt-5 bg-[#39FF14] hover:bg-[#5CFF45] text-[#0B0F17] font-semibold" data-testid="dashboard-save-pricing-button">Enregistrer les tarifs</Button>
      </div>
      <div className="card-surface rounded-xl p-6">
        <h3 className="font-display font-semibold text-slate-100">Forfaits prédéfinis</h3>
        <p className="text-sm text-slate-400 mt-1">Ex : aéroport Bordeaux-Mérignac.</p>
        <div className="space-y-2 mt-4">
          {packages.map((pk) => (
            <div key={pk.id} className="flex items-center justify-between rounded-lg bg-[#0B0F17] border border-[#1E293B] p-3">
              <p className="text-sm text-slate-200">{pk.label}</p>
              <div className="flex items-center gap-3"><span className="font-mono text-[#39FF14]">{pk.price} €</span><Button size="icon" variant="ghost" className="text-slate-500 hover:text-red-400 h-8 w-8" onClick={() => delPkg(pk.id)}><Trash2 className="h-4 w-4" /></Button></div>
            </div>
          ))}
        </div>
        <div className="flex gap-2 mt-4">
          <Input value={pkg.label} onChange={(e) => setPkg({ ...pkg, label: e.target.value })} placeholder="Libellé du forfait" className="bg-[#0B0F17] border-[#1E293B] text-slate-100" data-testid="package-label-input" />
          <Input type="number" value={pkg.price} onChange={(e) => setPkg({ ...pkg, price: e.target.value })} placeholder="€" className="w-24 bg-[#0B0F17] border-[#1E293B] text-slate-100" data-testid="package-price-input" />
          <Button onClick={addPkg} className="bg-[#1C2436] hover:bg-[#243049] text-slate-100 border border-[#1E293B]" data-testid="add-package-button"><Plus className="h-4 w-4" /></Button>
        </div>
      </div>
    </div>
  );
}

function PaymentsView({ bookings, stats }) {
  return (
    <div>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        <StatCard icon={Wallet} label="Encaissé" value={`${stats.paid.toFixed(2)} €`} gold />
        <StatCard icon={Euro} label="Revenu total" value={`${stats.revenue.toFixed(2)} €`} />
        <StatCard icon={TrendingUp} label="En attente (carte)" value={`${stats.pending.toFixed(2)} €`} />
      </div>
      <div className="card-surface rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#0B0F17] text-slate-500 text-xs uppercase tracking-wider">
              <tr><th className="text-left p-3">Réf.</th><th className="text-left p-3">Client</th><th className="text-left p-3">Date</th><th className="text-left p-3">Paiement</th><th className="text-right p-3">Montant</th><th className="text-right p-3">Facture</th></tr>
            </thead>
            <tbody>
              {bookings.map((b) => (
                <tr key={b.id} className="border-t border-[#1E293B] hover:bg-[#1C2436]">
                  <td className="p-3 font-mono text-[#39FF14]">{b.reference}</td>
                  <td className="p-3 text-slate-200">{b.client_name}</td>
                  <td className="p-3 text-slate-400">{b.date}</td>
                  <td className="p-3">
                    <Badge className={`border ${b.payment_status === "paid" ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" : "bg-amber-500/15 text-amber-300 border-amber-500/30"}`}>
                      {b.payment_status === "paid" ? "Payé" : (b.payment_method === "card" ? "En attente" : "À bord")}
                    </Badge>
                  </td>
                  <td className="p-3 text-right font-mono text-slate-100">{b.price?.toFixed(2)} €</td>
                  <td className="p-3 text-right">
                    <a href={`${API}/invoices/${b.id}`} target="_blank" rel="noreferrer">
                      <Button size="sm" variant="ghost" className="text-slate-300 hover:text-[#39FF14]" data-testid="dashboard-download-pdf-button"><FileText className="h-4 w-4" /></Button>
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
